import React, { useState, useEffect, useMemo } from 'react';
import { crmAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { User, Mail, Phone, Calendar, MessageSquare, Activity, Target, TrendingUp, AlertTriangle, CheckCircle, Plus, Send, Clock, Building2, Users, Tag, Briefcase, FileText, Check, X, Edit2, Maximize2, Minimize2, Download, DollarSign, Zap, ShoppingCart, Package, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { mapOpportunityBuyers } from './opportunityBuyerMapping';

// Sales Activity types (from Odoo is_sales_activity = True)
const SALES_ACTIVITY_TYPES = ["Demo", "RFP Submission", "Proof of concept", "Vendor Meeting", "Product Presentation", "POC", "Vendor Presentation"];

function SalesActivityBadge({ className = '' }) {
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#800000]/10 text-[#800000] border border-[#800000]/20 ${className}`}>
      <Zap className="h-2.5 w-2.5" />
      Sales Activity
    </span>
  );
}

const stageColors = {
  qualified: 'bg-blue-100 text-blue-700 border-blue-200',
  proposal: 'bg-purple-100 text-purple-700 border-purple-200',
  negotiation: 'bg-amber-100 text-amber-700 border-amber-200',
  closed_won: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed_lost: 'bg-red-100 text-red-700 border-red-200',
  review_negotiation: 'bg-amber-100 text-amber-700 border-amber-200',
  enquiry: 'bg-gray-100 text-gray-700 border-gray-200',
  won: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  lost: 'bg-red-100 text-red-700 border-red-200',
  hold: 'bg-gray-100 text-gray-600 border-gray-200',
};

const riskColors = {
  low: 'text-emerald-600 bg-emerald-50',
  medium: 'text-amber-600 bg-amber-50',
  high: 'text-red-600 bg-red-50',
};

const formatStage = (stage) => {
  if (!stage) return 'Unknown';
  const stageMap = {
    'review_negotiation': 'Review & Negotiation',
    'closed_won': 'Closed Won',
    'closed_lost': 'Closed Lost',
  };
  if (stageMap[stage.toLowerCase()]) return stageMap[stage.toLowerCase()];
  return stage.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

function OpportunityDetailSheet({ opportunity, open, onClose, formatCurrency, odooBaseUrl = '' }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [bluesheet, setBluesheet] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loadingSOs, setLoadingSOs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [newNoteType, setNewNoteType] = useState('general');
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({ type: 'Call', subject: '', description: '', date_deadline: '', assigned_user: '' });
  const [showSalesOnly, setShowSalesOnly] = useState(false);
  const [savingBluesheet, setSavingBluesheet] = useState(false);
  
  // Bluesheet form state
  const [bluesheetForm, setBluesheetForm] = useState({
    competition_status: 'unknown',
    budget_status: 'unknown',
    buying_influences: [],
    win_strategy: '',
    key_issues: [],
    commercial_buyer_name_override: '',
    technical_buyer_name_override: '',
    commercial_buyer_coach_override: null,
    technical_buyer_coach_override: null,
    rfp_invited_override: null,
    assisted_in_rfp_override: null,
    poc_demo_successful_override: null,
    pledge_level_override: '',
  });

  useEffect(() => {
    if (opportunity && open) {
      loadOpportunityDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity, open]);

  const loadOpportunityDetails = async () => {
    setLoading(true);
    try {
      const [bluesheetRes, activitiesRes, notesRes, logsRes] = await Promise.all([
        crmAPI.getBluesheet(opportunity.canonical_id),
        crmAPI.getOpportunityActivities(opportunity.canonical_id),
        crmAPI.getMessages(opportunity.canonical_id),
        crmAPI.getOpportunityLogs(opportunity.canonical_id),
      ]);
      
      setBluesheet(bluesheetRes.data);
      setActivities(activitiesRes.data || []);
      setNotes(notesRes.data || []);
      setLogs(logsRes.data || []);
      setSalesOrders([]);  // Reset; lazy-loaded when tab clicked
      
      // Initialize form with existing bluesheet data
      if (bluesheetRes.data?.bluesheet) {
        setBluesheetForm({
          competition_status: bluesheetRes.data.bluesheet.competition_status || 'unknown',
          budget_status: bluesheetRes.data.bluesheet.budget_status || 'unknown',
          buying_influences: bluesheetRes.data.bluesheet.buying_influences || [],
          win_strategy: bluesheetRes.data.bluesheet.win_strategy || '',
          key_issues: bluesheetRes.data.bluesheet.key_issues || [],
          commercial_buyer_name_override: bluesheetRes.data.bluesheet.commercial_buyer_name_override || '',
          technical_buyer_name_override: bluesheetRes.data.bluesheet.technical_buyer_name_override || '',
          commercial_buyer_coach_override: bluesheetRes.data.bluesheet.commercial_buyer_coach_override ?? null,
          technical_buyer_coach_override: bluesheetRes.data.bluesheet.technical_buyer_coach_override ?? null,
          rfp_invited_override: bluesheetRes.data.bluesheet.rfp_invited_override ?? null,
          assisted_in_rfp_override: bluesheetRes.data.bluesheet.assisted_in_rfp_override ?? null,
          poc_demo_successful_override: bluesheetRes.data.bluesheet.poc_demo_successful_override ?? null,
          pledge_level_override: bluesheetRes.data.bluesheet.pledge_level_override || '',
        });
      }
    } catch (error) {
      console.error('Error loading opportunity details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesOrders = async () => {
    if (!opportunity?.canonical_id) return;
    setLoadingSOs(true);
    try {
      const res = await crmAPI.getOpportunitySalesOrders(opportunity.canonical_id);
      setSalesOrders(res.data?.orders || []);
    } catch { toast.error('Failed to load sales orders'); }
    finally { setLoadingSOs(false); }
  };

  const handleSaveBluesheet = async () => {
    setSavingBluesheet(true);
    try {
      const res = await crmAPI.updateBluesheet(opportunity.canonical_id, bluesheetForm);
      setBluesheet({
        ...bluesheet,
        bluesheet: res.data.bluesheet,
        calculated_probability: res.data.calculated_probability,
      });
      toast.success('Bluesheet saved and probability updated');
    } catch (error) {
      toast.error('Failed to save bluesheet');
    } finally {
      setSavingBluesheet(false);
    }
  };

  const handleCreateActivity = async () => {
    if (!newActivity.subject.trim()) { toast.error('Subject is required'); return; }
    try {
      const res = await crmAPI.createOpportunityActivity(opportunity.canonical_id, newActivity);
      setActivities(prev => [res.data, ...prev]);
      setNewActivity({ type: 'Call', subject: '', description: '', date_deadline: '', assigned_user: '' });
      setShowAddActivity(false);
      toast.success('Activity created');
    } catch (error) {
      toast.error('Failed to create activity');
    }
  };

  const handleToggleActivity = async (activityId, completed) => {
    try {
      await crmAPI.updateOpportunityActivity(opportunity.canonical_id, activityId, { completed: !completed });
      setActivities(prev => prev.map(a => a.id === activityId ? { ...a, completed: !completed, status: !completed ? 'completed' : 'pending' } : a));
      toast.success(completed ? 'Activity reopened' : 'Activity completed');
    } catch (error) {
      toast.error('Failed to update activity');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const res = await crmAPI.createOpportunityNote(opportunity.canonical_id, {
        content: newNote,
        note_type: newNoteType,
      });
      setNotes([res.data, ...notes]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const addBuyingInfluence = (role) => {
    if (bluesheetForm.buying_influences.find(bi => bi.role === role)) return;
    
    setBluesheetForm({
      ...bluesheetForm,
      buying_influences: [
        ...bluesheetForm.buying_influences,
        { role, name: '', title: '', coverage: 0.5 }
      ]
    });
  };

  const updateBuyingInfluence = (index, field, value) => {
    const updated = [...bluesheetForm.buying_influences];
    updated[index] = { ...updated[index], [field]: value };
    setBluesheetForm({ ...bluesheetForm, buying_influences: updated });
  };

  const removeBuyingInfluence = (index) => {
    setBluesheetForm({
      ...bluesheetForm,
      buying_influences: bluesheetForm.buying_influences.filter((_, i) => i !== index)
    });
  };

  // State for fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);
  const buyerData = useMemo(() => opportunity ? mapOpportunityBuyers(opportunity) : { commercialBuyer: null, technicalBuyer: null, commercialBuyerCoach: false, technicalBuyerCoach: false }, [opportunity]);
  const bluesheetSettings = bluesheet?.settings || { weights: {}, role_profiles: {} };

  if (!opportunity) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent 
        className={`overflow-y-auto transition-all duration-300 ${isFullscreen ? 'w-full max-w-full sm:max-w-full' : 'w-full sm:max-w-2xl lg:max-w-3xl'}`}
        onDoubleClick={() => setIsFullscreen(!isFullscreen)}
      >
        <SheetHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="text-lg flex items-center gap-2">
                {opportunity.name}
                {opportunity.source_record_id && (
                  <a
                    href={`${odooBaseUrl || 'https://erp.securado.net'}/web#id=${opportunity.source_record_id}&model=crm.lead&view_type=form`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-[#800000] transition-colors"
                    title="Open in Odoo"
                    data-testid="opp-odoo-link"
                    onClick={e => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </SheetTitle>
              <div aria-describedby="opp-description" className="mt-1">
                <div id="opp-description" className="flex items-center gap-2 flex-wrap">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">{opportunity.account_name || 'No Account'}</span>
                  <Badge className={stageColors[opportunity.custom_stage?.toLowerCase().replace(/[&\s]/g, '_')] || stageColors[opportunity.stage] || 'bg-blue-100 text-blue-700'}>
                    {opportunity.custom_stage || formatStage(opportunity.stage)}
                  </Badge>
                  {opportunity.opportunity_number && (
                    <span className="text-xs font-mono text-gray-400">{opportunity.opportunity_number}</span>
                  )}
                </div>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-gray-500"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Probability Comparison Banner */}
          <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Salesperson Input</p>
                <p className="text-2xl font-bold text-blue-600">{opportunity.user_probability || opportunity.probability || 0}%</p>
              </div>
              <div className="h-12 w-px bg-gray-200"></div>
              <div className="text-center flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Bluesheet Score</p>
                <p className="text-2xl font-bold text-purple-600">{bluesheet?.calculated_probability?.probability || opportunity.automated_probability || 0}%</p>
              </div>
              <div className="h-12 w-px bg-gray-200"></div>
              <div className="text-center flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Variance</p>
                <p className={`text-2xl font-bold ${Math.abs((opportunity.user_probability || opportunity.probability || 0) - (bluesheet?.calculated_probability?.probability || opportunity.automated_probability || 0)) > 15 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {Math.abs((opportunity.user_probability || opportunity.probability || 0) - (bluesheet?.calculated_probability?.probability || opportunity.automated_probability || 0)).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </SheetHeader>
        
        <div className="mt-6">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'sales-orders' && salesOrders.length === 0) loadSalesOrders(); }}>
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="bluesheet">Bluesheet</TabsTrigger>
              <TabsTrigger value="sales-orders" data-testid="so-tab">
                <ShoppingCart className="h-3 w-3 mr-1" />Linked SOs
              </TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="logs">
                Logs
                {logs.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{logs.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              {/* 🔗 LINKED RECORDS PANEL — SO & Invoice cross-reference + alarms */}
              <LinkedRecordsPanel
                opportunity={opportunity}
                salesOrders={salesOrders}
                loadSalesOrders={loadSalesOrders}
                formatCurrency={formatCurrency}
                odooBaseUrl={odooBaseUrl}
                onNavigateToSO={() => { setActiveTab('sales-orders'); if (salesOrders.length === 0) loadSalesOrders(); }}
              />

              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-emerald-500" />
                      <span className="text-xs text-gray-500">Expected Revenue</span>
                    </div>
                    <p className="text-lg font-bold mt-1 truncate">
                      {formatCurrency(opportunity.amount || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1">
                      <Briefcase className="h-3 w-3 text-blue-500" />
                      <span className="text-xs text-gray-500">Sale Value</span>
                    </div>
                    <p className="text-lg font-bold mt-1 truncate">
                      {formatCurrency(opportunity.sale_value || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1">
                      <Target className="h-3 w-3 text-cyan-500" />
                      <span className="text-xs text-gray-500">User Probability</span>
                    </div>
                    <p className="text-lg font-bold mt-1">
                      {opportunity.user_probability || opportunity.probability || 0}%
                    </p>
                    <Progress value={opportunity.user_probability || opportunity.probability || 0} className="mt-2 h-1.5" />
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-amber-500" />
                      <span className="text-xs text-gray-500">Closing Date</span>
                    </div>
                    <p className="text-lg font-bold mt-1">
                      {opportunity.close_date ? new Date(opportunity.close_date).toLocaleDateString() : 'Not set'}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Contact & Sales Team Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Sales Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-24">Salesperson:</span>
                      <span className="text-sm font-medium">{opportunity.owner_name || 'Unassigned'}</span>
                    </div>
                    {opportunity.team_id && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Sales Team:</span>
                        <Badge variant="outline" className="text-xs">
                          <Users className="h-3 w-3 mr-1" />
                          {Array.isArray(opportunity.team_id) ? opportunity.team_id[1] : opportunity.team_id}
                        </Badge>
                      </div>
                    )}
                    {opportunity.deal_type && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Deal Type:</span>
                        <Badge variant="secondary" className="text-xs">{opportunity.deal_type}</Badge>
                      </div>
                    )}
                    {opportunity.priority && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Priority:</span>
                        <div className="flex gap-1">
                          {[1, 2, 3].map(star => (
                            <span key={star} className={star <= opportunity.priority ? 'text-amber-400' : 'text-gray-200'}>★</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {opportunity.presales_engineer && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Pre-sales:</span>
                        <span className="text-sm font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded">{opportunity.presales_engineer}</span>
                        {opportunity.presales_contribution && <Badge variant="outline" className="text-[10px]">{opportunity.presales_contribution}</Badge>}
                      </div>
                    )}
                    {opportunity.lead_source && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Lead Source:</span>
                        <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{opportunity.lead_source}</span>
                      </div>
                    )}
                    {opportunity.campaign_name && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-24">Campaign:</span>
                        <span className="text-sm font-medium">{opportunity.campaign_name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {opportunity.customer_name && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 w-20">Company:</span>
                        <span className="text-sm font-medium">{opportunity.customer_name}</span>
                      </div>
                    )}
                    {(opportunity.contact_email || opportunity.contact_id) && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{opportunity.contact_email || (Array.isArray(opportunity.contact_id) ? opportunity.contact_id[1] : 'N/A')}</span>
                      </div>
                    )}
                    {(opportunity.contact_phone || opportunity.contact_mobile) && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{opportunity.contact_phone || opportunity.contact_mobile}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Buying Influences & Tags */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Key Contacts (Buying Influences)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {buyerData.commercialBuyer && (
                      <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{buyerData.commercialBuyer}</p>
                          <p className="text-xs text-gray-500">Commercial Buyer</p>
                        </div>
                        {buyerData.commercialBuyerCoach && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Coach</Badge>
                        )}
                      </div>
                    )}
                    {buyerData.technicalBuyer && (
                      <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                        <div>
                          <p className="text-sm font-medium">{buyerData.technicalBuyer}</p>
                          <p className="text-xs text-gray-500">Technical Buyer</p>
                        </div>
                        {buyerData.technicalBuyerCoach && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Coach</Badge>
                        )}
                      </div>
                    )}
                    {!buyerData.commercialBuyer && !buyerData.technicalBuyer && (
                      <p className="text-sm text-gray-500 text-center py-4">No buying influences identified</p>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Deal Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {opportunity.is_tender && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Is Tender:</span>
                        <Badge variant={opportunity.is_tender === 'Yes' ? 'default' : 'secondary'} className="text-xs">
                          {opportunity.is_tender}
                        </Badge>
                      </div>
                    )}
                    {opportunity.budget_status && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Budget Status:</span>
                        <span className="font-medium">{opportunity.budget_status}</span>
                      </div>
                    )}
                    {opportunity.pledge && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Pledge/Commitment:</span>
                        <Badge variant={opportunity.pledge === 'No Commitment' ? 'secondary' : 'default'} className="text-xs">
                          {opportunity.pledge}
                        </Badge>
                      </div>
                    )}
                    {opportunity.rfp_invited !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">RFP Invited:</span>
                        <Badge variant={opportunity.rfp_invited ? 'default' : 'secondary'} className="text-xs">
                          {opportunity.rfp_invited ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    )}
                    {opportunity.assisted_in_rfp !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Assisted in RFP:</span>
                        <Badge variant={opportunity.assisted_in_rfp ? 'default' : 'secondary'} className="text-xs">
                          {opportunity.assisted_in_rfp ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    )}
                    {opportunity.poc_demo_done !== undefined && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">POC/Demo Done Successfully:</span>
                        <Badge variant={opportunity.poc_demo_done ? 'default' : 'secondary'} className="text-xs">
                          {opportunity.poc_demo_done ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Competitor Information */}
              {(opportunity.competitor_solution || opportunity.competitor_price) && (
                <Card className="border-red-200 bg-red-50/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      Competitor Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {opportunity.competitor_solution && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Competitor's Solution:</span>
                        <Badge variant="outline" className="text-red-600 border-red-300">{opportunity.competitor_solution}</Badge>
                      </div>
                    )}
                    {opportunity.competitor_price && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Competitor's Price:</span>
                        <span className="font-mono text-sm text-red-600">{opportunity.competitor_price}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Tender Deadlines */}
              {(opportunity.tender_submission_deadline || opportunity.query_submission_deadline || opportunity.tender_purchase_deadline) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Important Deadlines
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-3 gap-4 text-sm">
                    {opportunity.query_submission_deadline && (
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500">Query Deadline</p>
                        <p className="font-medium">{new Date(opportunity.query_submission_deadline).toLocaleDateString()}</p>
                      </div>
                    )}
                    {opportunity.tender_submission_deadline && (
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <p className="text-xs text-gray-500">Tender Submission</p>
                        <p className="font-medium">{new Date(opportunity.tender_submission_deadline).toLocaleDateString()}</p>
                      </div>
                    )}
                    {opportunity.tender_purchase_deadline && (
                      <div className="text-center p-2 bg-amber-50 rounded">
                        <p className="text-xs text-gray-500">Purchase Deadline</p>
                        <p className="font-medium">{new Date(opportunity.tender_purchase_deadline).toLocaleDateString()}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Description */}
              {opportunity.descriptions && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">{opportunity.descriptions}</p>
                  </CardContent>
                </Card>
              )}
              
              {bluesheet?.calculated_probability && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Bluesheet Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Calculated Probability</span>
                      <Badge className={riskColors[bluesheet.calculated_probability.risk_level]}>
                        {bluesheet.calculated_probability.probability}% ({bluesheet.calculated_probability.risk_level} risk)
                      </Badge>
                    </div>
                    {bluesheet.calculated_probability.recommendations?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Recommendations:</p>
                        <ul className="space-y-1">
                          {bluesheet.calculated_probability.recommendations.map((rec, i) => (
                            <li key={i} className="text-xs flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 text-amber-500" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Bluesheet Tab - Enhanced with manual input + history */}
            <TabsContent value="bluesheet" className="mt-4 space-y-4">
              {/* Probability Scores Comparison */}
              <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Salesperson Input</p>
                      <p className="text-3xl font-bold text-blue-600">{opportunity.user_probability || opportunity.probability || 0}%</p>
                      <p className="text-xs text-gray-400">Manual estimate</p>
                    </div>
                    <div className="border-l border-r border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">AI Confidence</p>
                      <p className="text-3xl font-bold text-purple-600">{opportunity.automated_probability || 0}%</p>
                      <p className="text-xs text-gray-400">From Odoo AI</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Bluesheet Score</p>
                      <p className="text-3xl font-bold text-emerald-600">{bluesheet?.calculated_probability?.probability || 0}%</p>
                      <Badge className={riskColors[bluesheet?.calculated_probability?.risk_level || 'medium']} >
                        {bluesheet?.calculated_probability?.risk_level || 'calculating'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Score Breakdown */}
              {bluesheet?.calculated_probability && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Score Breakdown (Bluesheet Calculation)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(bluesheet.calculated_probability.breakdown || {}).map(([key, score]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            <Progress value={score} className="w-24 h-2" />
                            <span className="font-medium w-10 text-right">{score}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {bluesheet?.calculated_probability?.profile && (
                <Card className="border-slate-200 bg-slate-50/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Scoring Profile</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Active profile</span>
                      <Badge variant="outline" className="capitalize">{bluesheet.calculated_probability.profile.replace('_', ' ')}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {Object.entries(bluesheet.calculated_probability.weights || {}).map(([key, value]) => (
                        <div key={key} className="p-2 rounded-lg border bg-white flex items-center justify-between">
                          <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className="font-semibold text-slate-800">{Math.round(value * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {bluesheet?.calculated_probability && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-emerald-200 bg-emerald-50/40">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-emerald-700">Positive Drivers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(bluesheet.calculated_probability.positive_drivers || []).length === 0 ? (
                        <p className="text-xs text-slate-500">No strong positive drivers yet.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {bluesheet.calculated_probability.positive_drivers.map((driver, index) => (
                            <li key={index} className="text-xs flex items-start gap-2 text-slate-700"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5" />{driver}</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50/40">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-amber-700">Missing / Weak Signals</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(bluesheet.calculated_probability.missing_signals || []).length === 0 ? (
                        <p className="text-xs text-slate-500">No major missing signals detected.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {bluesheet.calculated_probability.missing_signals.map((signal, index) => (
                            <li key={index} className="text-xs flex items-start gap-2 text-slate-700"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />{signal}</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {bluesheet?.calculated_probability && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <Card className="border-emerald-200 bg-emerald-50/40">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-emerald-700">Positive Drivers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(bluesheet.calculated_probability.positive_drivers || []).length === 0 ? (
                        <p className="text-xs text-slate-500">No strong positive drivers yet.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {bluesheet.calculated_probability.positive_drivers.map((driver, index) => (
                            <li key={index} className="text-xs flex items-start gap-2 text-slate-700"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5" />{driver}</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50/40">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-amber-700">Missing / Weak Signals</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(bluesheet.calculated_probability.missing_signals || []).length === 0 ? (
                        <p className="text-xs text-slate-500">No major missing signals detected.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {bluesheet.calculated_probability.missing_signals.map((signal, index) => (
                            <li key={index} className="text-xs flex items-start gap-2 text-slate-700"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />{signal}</li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* Synced from Odoo - Read Only Display */}
              <Card className="border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="h-4 w-4" />
                    Data from Odoo (Auto-synced)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {/* Buying Influences */}
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Commercial Buyer</p>
                      <p className="font-medium">{buyerData.commercialBuyer || 'Not identified'}</p>
                      {buyerData.commercialBuyerCoach && <Badge className="text-xs bg-emerald-100 text-emerald-700">Coach</Badge>}
                      {buyerData.commercialBuyerId && <p className="text-[11px] text-slate-400 mt-1">ID: {buyerData.commercialBuyerId}</p>}
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Technical Buyer</p>
                      <p className="font-medium">{buyerData.technicalBuyer || 'Not identified'}</p>
                      {buyerData.technicalBuyerCoach && <Badge className="text-xs bg-emerald-100 text-emerald-700">Coach</Badge>}
                      {buyerData.technicalBuyerId && <p className="text-[11px] text-slate-400 mt-1">ID: {buyerData.technicalBuyerId}</p>}
                    </div>
                    {/* Budget & Competition */}
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Budget Status</p>
                      <p className="font-medium">{opportunity.budget_status || 'Unknown'}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Competitor</p>
                      <p className="font-medium text-red-600">{opportunity.competitor_solution || 'None'}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Pledge/Commitment</p>
                      <p className="font-medium">{opportunity.pledge || 'Unknown'}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Closing Date</p>
                      <p className="font-medium">{opportunity.close_date ? new Date(opportunity.close_date).toLocaleDateString() : 'Not set'}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">Assisted in RFP</p>
                      <p className="font-medium">{opportunity.assisted_in_rfp ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500 mb-1">POC/Demo Done Successfully</p>
                      <p className="font-medium">{opportunity.poc_demo_done ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Manual Input Form */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Edit2 className="h-4 w-4" />
                    Manual Input (Override/Add)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Competition Status Override */}
                  <div className="space-y-2">
                    <Label className="text-xs">Competition Status (Override)</Label>
                    <Select 
                      value={bluesheetForm.competition_status} 
                      onValueChange={(v) => setBluesheetForm({...bluesheetForm, competition_status: v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Use Odoo data (default)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Use Odoo data (default)</SelectItem>
                        <SelectItem value="sole_source">Sole Source (100%)</SelectItem>
                        <SelectItem value="favored">Favored Position (75%)</SelectItem>
                        <SelectItem value="even">Even Competition (50%)</SelectItem>
                        <SelectItem value="behind">Behind Competitor (25%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Budget Status Override */}
                  <div className="space-y-2">
                    <Label className="text-xs">Budget Status (Override)</Label>
                    <Select 
                      value={bluesheetForm.budget_status} 
                      onValueChange={(v) => setBluesheetForm({...bluesheetForm, budget_status: v})}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Use Odoo data (default)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Use Odoo data (default)</SelectItem>
                        <SelectItem value="confirmed">Budget Confirmed (100%)</SelectItem>
                        <SelectItem value="identified">Budget Identified (75%)</SelectItem>
                        <SelectItem value="in_process">Budget In Process (50%)</SelectItem>
                        <SelectItem value="not_identified">Not Identified (25%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Additional Buying Influences */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Additional Buying Influences</Label>
                      <Select onValueChange={addBuyingInfluence}>
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue placeholder="Add..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="economic_buyer">Economic Buyer</SelectItem>
                          <SelectItem value="user_buyer">User Buyer</SelectItem>
                          <SelectItem value="technical_buyer">Technical Buyer</SelectItem>
                          <SelectItem value="coach">Coach/Champion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {bluesheetForm.buying_influences.length > 0 && (
                      <div className="space-y-2">
                        {bluesheetForm.buying_influences.map((bi, index) => (
                          <div key={index} className="p-2 border rounded bg-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">{bi.role.replace('_', ' ')}</Badge>
                              <Input
                                placeholder="Name"
                                value={bi.name || ''}
                                onChange={(e) => updateBuyingInfluence(index, 'name', e.target.value)}
                                className="h-7 w-32 text-xs"
                              />
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeBuyingInfluence(index)} className="h-6 px-2 text-red-500">
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Commercial Buyer Override</Label>
                      <Input value={bluesheetForm.commercial_buyer_name_override} onChange={(e) => setBluesheetForm({...bluesheetForm, commercial_buyer_name_override: e.target.value})} placeholder={buyerData.commercialBuyer || 'Use Odoo buyer'} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Technical Buyer Override</Label>
                      <Input value={bluesheetForm.technical_buyer_name_override} onChange={(e) => setBluesheetForm({...bluesheetForm, technical_buyer_name_override: e.target.value})} placeholder={buyerData.technicalBuyer || 'Use Odoo buyer'} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Commercial Buyer Coach</Label>
                      <Select value={bluesheetForm.commercial_buyer_coach_override === null ? 'use_odoo' : bluesheetForm.commercial_buyer_coach_override ? 'yes' : 'no'} onValueChange={(v) => setBluesheetForm({...bluesheetForm, commercial_buyer_coach_override: v === 'use_odoo' ? null : v === 'yes'})}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="use_odoo">Use Odoo</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Technical Buyer Coach</Label>
                      <Select value={bluesheetForm.technical_buyer_coach_override === null ? 'use_odoo' : bluesheetForm.technical_buyer_coach_override ? 'yes' : 'no'} onValueChange={(v) => setBluesheetForm({...bluesheetForm, technical_buyer_coach_override: v === 'use_odoo' ? null : v === 'yes'})}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="use_odoo">Use Odoo</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">RFP Invited</Label>
                      <Select value={bluesheetForm.rfp_invited_override === null ? 'use_odoo' : bluesheetForm.rfp_invited_override ? 'yes' : 'no'} onValueChange={(v) => setBluesheetForm({...bluesheetForm, rfp_invited_override: v === 'use_odoo' ? null : v === 'yes'})}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="use_odoo">Use Odoo</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Assisted in RFP</Label>
                      <Select value={bluesheetForm.assisted_in_rfp_override === null ? 'use_odoo' : bluesheetForm.assisted_in_rfp_override ? 'yes' : 'no'} onValueChange={(v) => setBluesheetForm({...bluesheetForm, assisted_in_rfp_override: v === 'use_odoo' ? null : v === 'yes'})}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="use_odoo">Use Odoo</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">POC / Demo Successful</Label>
                      <Select value={bluesheetForm.poc_demo_successful_override === null ? 'use_odoo' : bluesheetForm.poc_demo_successful_override ? 'yes' : 'no'} onValueChange={(v) => setBluesheetForm({...bluesheetForm, poc_demo_successful_override: v === 'use_odoo' ? null : v === 'yes'})}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="use_odoo">Use Odoo</SelectItem><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Pledge / Commitment Override</Label>
                      <Input value={bluesheetForm.pledge_level_override} onChange={(e) => setBluesheetForm({...bluesheetForm, pledge_level_override: e.target.value})} placeholder={opportunity.pledge || 'Use Odoo pledge'} />
                    </div>
                  </div>
                  
                  {/* Win Strategy */}
                  <div className="space-y-2">
                    <Label className="text-xs">Win Strategy / Action Plan</Label>
                    <Textarea
                      value={bluesheetForm.win_strategy}
                      onChange={(e) => setBluesheetForm({...bluesheetForm, win_strategy: e.target.value})}
                      placeholder="Document your strategy to win this deal..."
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                  
                  <Button onClick={handleSaveBluesheet} disabled={savingBluesheet} className="w-full">
                    {savingBluesheet ? 'Saving...' : 'Save & Recalculate'}
                  </Button>
                </CardContent>
              </Card>
              
              {/* AI Recommendations */}
              {bluesheet?.calculated_probability?.recommendations?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" />
                      AI Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1">
                      {bluesheet.calculated_probability.recommendations.map((rec, i) => (
                        <li key={i} className="text-xs flex items-start gap-2 p-1.5 bg-amber-50 rounded">
                          <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              
              {/* Calculation History Log */}
              <Card className="border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    Calculation History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xs space-y-2 max-h-48 overflow-y-auto">
                    {/* Current Calculation */}
                    <div className="p-2 bg-blue-50 rounded border border-blue-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-blue-700">Latest Calculation</span>
                        <span className="text-gray-400">{new Date().toLocaleString()}</span>
                      </div>
                      <div className="font-mono text-gray-600 space-y-0.5">
                        <p>📊 Stage: {opportunity.custom_stage || opportunity.stage} → {bluesheet?.calculated_probability?.breakdown?.stage_score || 0}pts</p>
                        <p>👥 Buyers: {buyerData.commercialBuyer || 'None'}{buyerData.technicalBuyer ? `, ${buyerData.technicalBuyer}` : ''} → {bluesheet?.calculated_probability?.breakdown?.buying_influences_score || 0}pts</p>
                        <p>⚔️ Competition: {opportunity.competitor_solution || 'None'} → {bluesheet?.calculated_probability?.breakdown?.competition_score || 0}pts</p>
                        <p>💰 Budget: {opportunity.budget_status || 'Unknown'} → {bluesheet?.calculated_probability?.breakdown?.budget_score || 0}pts</p>
                        <p>📅 Timeline: {opportunity.close_date ? new Date(opportunity.close_date).toLocaleDateString() : 'Not set'} → {bluesheet?.calculated_probability?.breakdown?.timeline_score || 0}pts</p>
                        <p>📈 Activity: {activities.length} activities → {bluesheet?.calculated_probability?.breakdown?.activity_score || 0}pts</p>
                        <p className="font-bold text-blue-700 pt-1 border-t border-blue-200">🎯 Result: {bluesheet?.calculated_probability?.probability || 0}% ({bluesheet?.calculated_probability?.risk_level} risk)</p>
                      </div>
                    </div>
                    
                    {/* Historical note */}
                    <p className="text-center text-gray-400 italic py-2">
                      Odoo AI last calculated: {opportunity.automated_probability || 0}% probability
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Sales Orders Tab */}
            <TabsContent value="sales-orders" className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-sm">Linked Sales Orders</h3>
                  {salesOrders.length > 0 && <Badge variant="secondary" className="text-[10px]">{salesOrders.length}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadSalesOrders} data-testid="so-refresh-btn">
                    <ShoppingCart className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open('/sales-orders', '_blank')} data-testid="open-full-so-module-btn">
                    <ExternalLink className="h-3 w-3 mr-1" /> Full SO Module
                  </Button>
                </div>
              </div>
              <div className="mb-3 rounded-lg border border-dashed bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="opportunity-so-summary-note">
                This is a linked-record summary for the current opportunity. Use the full Sales Orders module for cross-account comparisons, billing alerts, and broader SO analytics.
              </div>
              {loadingSOs ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
              ) : salesOrders.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No sales orders linked to this opportunity</p>
                  <p className="text-xs mt-1 text-gray-300">SOs created in Odoo for this opportunity will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary row */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'Total SO Value', value: formatCurrency(salesOrders.reduce((s, o) => s + (o.amount_total || 0), 0)), color: 'text-foreground' },
                      { label: 'Total Margin', value: formatCurrency(salesOrders.reduce((s, o) => s + (o.margin || 0), 0)), color: 'text-emerald-600' },
                      { label: 'Fully Invoiced', value: salesOrders.filter(o => (o.invoice_status || '').toLowerCase() === 'fully_invoiced').length + ' / ' + salesOrders.length, color: 'text-[#800000]' },
                      { label: 'Partially Invoiced', value: salesOrders.filter(o => (o.invoice_status || '').toLowerCase() === 'partially_invoiced').length, color: 'text-amber-600' },
                    ].map(kpi => (
                      <div key={kpi.label} className="p-2 rounded-lg bg-muted/40 text-center">
                        <p className="text-[10px] text-muted-foreground">{kpi.label}</p>
                        <p className={`text-sm font-bold ${kpi.color}`}>{kpi.value}</p>
                      </div>
                    ))}
                  </div>
                  {/* SO cards */}
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 pr-1">
                      {salesOrders.map((so, i) => {
                        const bs = {
                          fully_invoiced: 'bg-emerald-100 text-emerald-700',
                          partially_invoiced: 'bg-amber-100 text-amber-700',
                          not_invoiced: 'bg-slate-100 text-slate-700',
                        }[(so.invoice_status || '').toLowerCase()] || 'bg-slate-100 text-slate-700';
                        return (
                          <div key={i} className="p-3 rounded-lg border bg-muted/20 space-y-2" data-testid={`opp-so-card-${so.so_number}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono font-bold text-sm text-[#800000]">{so.so_number}</span>
                                {so.source_record_id && (
                                  <a href={`${odooBaseUrl || 'https://erp.securado.net'}/web#id=${so.source_record_id}&model=sale.order&view_type=form`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-muted-foreground hover:text-[#800000]" title="Open in Odoo"
                                    data-testid={`so-odoo-link-${so.so_number}`}>
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                              <Badge className={`text-xs border-0 ${bs}`}>{so.billing_label}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              <div><span className="text-muted-foreground">Salesperson: </span><span className="font-medium">{so.salesperson || '—'}</span></div>
                              <div><span className="text-muted-foreground">Created By: </span><span className="font-medium">{so.created_by || '—'}</span></div>
                              <div><span className="text-muted-foreground">Amount: </span><span className="font-semibold">{formatCurrency(so.amount_total)}</span></div>
                              <div><span className="text-muted-foreground">Margin: </span><span className="font-semibold text-emerald-600">{formatCurrency(so.margin)} ({(so.margin_percent || 0).toFixed(1)}%)</span></div>
                              <div><span className="text-muted-foreground">Order Date: </span>{so.order_date ? new Date(so.order_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</div>
                              {so.customer_ref && <div><span className="text-muted-foreground">Cust. Ref: </span><span>{so.customer_ref}</span></div>}
                              {so.payment_terms && <div className="col-span-2"><span className="text-muted-foreground">Payment Terms: </span><span className="font-semibold text-blue-700">{so.payment_terms}</span></div>}
                              {so.contract_start_date && <div><span className="text-muted-foreground">Contract Start: </span><span>{so.contract_start_date}</span></div>}
                              {so.contract_end_date && <div><span className="text-muted-foreground">Contract End: </span><span>{so.contract_end_date}</span></div>}
                            </div>
                            {!so.payment_terms && !so.customer_payment_term && (
                              <div className="flex items-center gap-1.5 text-[10px] text-red-600 bg-red-50 rounded px-2 py-1 mt-1">
                                <AlertTriangle className="h-3 w-3 shrink-0" />No payment terms recorded on this SO
                              </div>
                            )}
                            {/* SO lines summary */}
                            {so.lines?.length > 0 && (
                              <div className="border-t pt-2 mt-1">
                                <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1"><Package className="h-2.5 w-2.5" /> {so.lines.length} line item{so.lines.length !== 1 ? 's' : ''}</p>
                                <div className="space-y-0.5">
                                  {so.lines.slice(0, 3).map((l, j) => (
                                    <div key={j} className="flex justify-between text-[10px] text-muted-foreground">
                                      <span className="truncate mr-2">{(l.product_name && !String(l.product_name).startsWith("['")) ? l.product_name : (l.description || l.product_name || 'Product')}</span>
                                      <span className="font-mono whitespace-nowrap">{formatCurrency(l.subtotal)}</span>
                                    </div>
                                  ))}
                                  {so.lines.length > 3 && <p className="text-[10px] text-muted-foreground">+{so.lines.length - 3} more lines</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </TabsContent>
            
            {/* Activities Tab */}
            <TabsContent value="activities" className="mt-4">
              {/* Activities tab — with Sales Activity filter */}
              {(() => {
                const visibleActivities = showSalesOnly
                  ? activities.filter(a => SALES_ACTIVITY_TYPES.includes(a.activity_type) || a.is_sales_activity)
                  : activities;
                const salesCount = activities.filter(a => SALES_ACTIVITY_TYPES.includes(a.activity_type) || a.is_sales_activity).length;

                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-sm">Activities</h3>
                        <Badge variant="secondary" className="text-[10px]">{activities.length}</Badge>
                        <button
                          onClick={() => setShowSalesOnly(!showSalesOnly)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all border ${showSalesOnly ? 'bg-[#800000] text-white border-[#800000]' : 'bg-[#800000]/5 text-[#800000] border-[#800000]/20 hover:bg-[#800000]/10'}`}
                          data-testid="sales-only-toggle"
                        >
                          <Zap className="h-2.5 w-2.5" />
                          Sales Only ({salesCount})
                        </button>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setShowAddActivity(!showAddActivity)} data-testid="add-activity-btn" className="h-7 text-xs">
                        <Plus className="h-3 w-3 mr-1" />{showAddActivity ? 'Cancel' : 'Add Activity'}
                      </Button>
                    </div>

                    {showAddActivity && (
                      <Card className="mb-4 border-blue-200 bg-blue-50/30">
                        <CardContent className="p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Type</Label>
                              <Select value={newActivity.type} onValueChange={v => setNewActivity(p => ({ ...p, type: v }))}>
                                <SelectTrigger className="h-8 text-sm bg-white"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <div className="px-2 py-1 text-[10px] font-bold text-[#800000] uppercase tracking-wide flex items-center gap-1"><Zap className="h-2.5 w-2.5" /> Sales Activities</div>
                                  {SALES_ACTIVITY_TYPES.map(t => (
                                    <SelectItem key={t} value={t}>
                                      <span className="flex items-center gap-1.5">{t} <span className="text-[8px] bg-[#800000]/10 text-[#800000] px-1 rounded">KPI</span></span>
                                    </SelectItem>
                                  ))}
                                  <div className="px-2 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-1 border-t">Other</div>
                                  {['Call', 'Email', 'Meeting', 'To Do'].map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {SALES_ACTIVITY_TYPES.includes(newActivity.type) && (
                                <p className="text-[10px] text-[#800000] mt-1 flex items-center gap-1">
                                  <Zap className="h-2.5 w-2.5" /> This will be counted as a Sales Activity for KPI
                                </p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs">Deadline</Label>
                              <Input type="date" className="h-8 text-sm bg-white" value={newActivity.date_deadline} onChange={e => setNewActivity(p => ({ ...p, date_deadline: e.target.value }))} />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Subject</Label>
                            <Input className="h-8 text-sm bg-white" placeholder="Activity subject..." value={newActivity.subject} onChange={e => setNewActivity(p => ({ ...p, subject: e.target.value }))} data-testid="activity-subject-input" />
                          </div>
                          <div>
                            <Label className="text-xs">Description</Label>
                            <Textarea className="text-sm bg-white resize-none" rows={2} placeholder="Optional details..." value={newActivity.description} onChange={e => setNewActivity(p => ({ ...p, description: e.target.value }))} />
                          </div>
                          <div>
                            <Label className="text-xs">Assigned To</Label>
                            <Input className="h-8 text-sm bg-white" placeholder="Name..." value={newActivity.assigned_user} onChange={e => setNewActivity(p => ({ ...p, assigned_user: e.target.value }))} />
                          </div>
                          <Button size="sm" onClick={handleCreateActivity} className="bg-[#800000] hover:bg-[#600000] text-white" data-testid="save-activity-btn">
                            <Plus className="h-3 w-3 mr-1" /> Create Activity
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    <ScrollArea className="h-[450px]">
                      <div className="space-y-2">
                        {visibleActivities.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <Zap className="h-8 w-8 mx-auto mb-2 opacity-30 text-[#800000]" />
                            <p className="text-sm">{showSalesOnly ? 'No Sales Activities' : 'No activities yet'}</p>
                            {showSalesOnly && (
                              <p className="text-xs mt-1 text-gray-400">
                                Sales activities: {SALES_ACTIVITY_TYPES.join(', ')}
                              </p>
                            )}
                          </div>
                        ) : visibleActivities.map((activity) => {
                          const typeColors = {
                            call: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-l-blue-400', icon: '📞' },
                            email: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-l-purple-400', icon: '✉️' },
                            meeting: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-l-amber-400', icon: '🤝' },
                            'proof of concept': { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-l-cyan-400', icon: '🔬' },
                            'site visit': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-l-green-400', icon: '🏢' },
                            'work shop': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-l-orange-400', icon: '🛠️' },
                            demo: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-l-indigo-400', icon: '🖥️' },
                            'rfp submission': { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-l-rose-400', icon: '📄' },
                            'vendor meeting': { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-l-teal-400', icon: '🤝' },
                            'product presentation': { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-l-violet-400', icon: '📊' },
                            poc: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-l-cyan-400', icon: '🔬' },
                            'vendor presentation': { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-l-violet-400', icon: '📊' },
                          };
                          const typeLower = (activity.activity_type || activity.type || '').toLowerCase();
                          const cfg = typeColors[typeLower] || { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-l-gray-300', icon: '📋' };
                          
                          const isCompleted = activity.completed || activity.status === 'done';
                          const isOverdue = activity.was_overdue || activity.status === 'overdue';
                          const isPending = !isCompleted && !isOverdue;
                          const isSales = activity.is_sales_activity || SALES_ACTIVITY_TYPES.includes(activity.activity_type);
                          
                          const borderClass = isSales ? 'border-l-[#800000]' : isCompleted ? cfg.border : isOverdue ? 'border-l-red-500' : 'border-l-gray-300';
                          
                          const completionStr = activity.completion_date
                            ? new Date(activity.completion_date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : null;
                          const dueStr = (activity.due_date || activity.date_deadline)
                            ? new Date(activity.due_date || activity.date_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : null;
                          
                          return (
                            <div key={activity.id}
                              className="rounded-xl overflow-hidden transition-all hover:shadow-sm"
                              style={{
                                background: isCompleted ? 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' :
                                             isOverdue ? 'linear-gradient(135deg, #fff7f7 0%, #fef2f2 100%)' :
                                             'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)',
                                border: `1px solid ${isCompleted ? '#bbf7d0' : isOverdue ? '#fecaca' : '#e0e7ff'}`,
                                borderLeft: `3px solid ${cfg.borderColor || borderClass.includes('blue') ? '#3b82f6' : borderClass.includes('green') ? '#10b981' : borderClass.includes('purple') ? '#8b5cf6' : '#800000'}`,
                              }}
                              data-testid={`activity-item-${activity.id}`}>
                              <div className="flex items-center gap-2.5 px-3 py-2.5">
                                {/* Activity type icon pill */}
                                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base shrink-0"
                                  style={{ background: isCompleted ? '#dcfce7' : isOverdue ? '#fee2e2' : '#e0e7ff' }}>
                                  {cfg.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className={`text-xs font-bold uppercase tracking-wide ${cfg.text}`}>
                                      {activity.activity_type || activity.type || 'Activity'}
                                    </span>
                                    {isSales && <SalesActivityBadge />}
                                  </div>
                                  {activity.subject && (
                                    <p className="text-sm font-semibold text-slate-800 leading-tight mt-0.5 truncate">{activity.subject}</p>
                                  )}
                                </div>
                                {/* Status badge */}
                                <div className="shrink-0">
                                  {isCompleted && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                                      <Check className="h-2.5 w-2.5" />Done
                                    </span>
                                  )}
                                  {isOverdue && !isCompleted && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-500 text-white px-2 py-0.5 rounded-full">
                                      ⚠ Late
                                    </span>
                                  )}
                                  {isPending && !isCompleted && (
                                    <span className="inline-flex items-center text-[10px] font-semibold bg-amber-400 text-white px-2 py-0.5 rounded-full">
                                      Planned
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* Body */}
                              {activity.description && (
                                <div className="px-3 pb-2.5">
                                  <p className="text-xs text-slate-600 leading-relaxed bg-white/70 rounded-lg p-2 border border-white">{activity.description}</p>
                                </div>
                              )}
                              {/* Meta */}
                              <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 pb-2.5 text-[10px]">
                                {activity.assigned_to && (
                                  <span className="flex items-center gap-1 text-slate-500">
                                    <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[9px]">
                                      {activity.assigned_to[0]}
                                    </span>
                                    {activity.assigned_to}
                                  </span>
                                )}
                                {completionStr && (
                                  <span className="text-emerald-600 font-medium flex items-center gap-0.5">✓ {completionStr}</span>
                                )}
                                {dueStr && (
                                  <span className={`flex items-center gap-0.5 font-medium ${isOverdue ? 'text-red-500' : 'text-slate-400'}`}>
                                    Due: {dueStr}
                                  </span>
                                )}
                                {activity.source_system === 'local' && !activity.completed && (
                                  <button onClick={() => handleToggleActivity(activity.id, activity.completed)}
                                    className="text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 ml-auto"
                                    data-testid={`toggle-activity-${activity.id}`}>
                                    <Check className="h-3 w-3" /> Mark Done
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </>
                );
              })()}
            </TabsContent>
            
            {/* Logs Tab - Chatter History */}
            <TabsContent value="logs" className="mt-4">
              {/* Rich Odoo-faithful chatter — using stored log_messages */}
              {(() => {
                // Group logs by date and render richly
                const AVATAR_COLORS = ['#7C3AED','#DC2626','#D97706','#059669','#2563EB','#DB2777','#0891B2','#65A30D'];
                const avatarColor = (name='') => { const code=[...name].reduce((a,c)=>a+c.charCodeAt(0),0); return AVATAR_COLORS[code%AVATAR_COLORS.length]; };
                const initials = (name='') => { const p=name.trim().split(/\s+/); return p.length>=2?(p[0][0]+p[1][0]).toUpperCase():(name[0]||'?').toUpperCase(); };
                const relTime = (d) => { if(!d) return ''; const ms=Date.now()-new Date(d); const mins=Math.floor(ms/60000),hrs=Math.floor(ms/3600000),days=Math.floor(ms/86400000),mos=Math.floor(days/30); if(mins<1) return 'just now'; if(mins<60) return `${mins}m ago`; if(hrs<24) return `${hrs}h ago`; if(days<31) return `${days} day${days!==1?'s':''} ago`; return `${mos} month${mos!==1?'s':''} ago`; };
                const fmtGroupDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}) : '';
                
                // Group by date
                const grouped = {};
                for (const log of logs) {
                  const key = (log.date||'').slice(0,10);
                  if (!grouped[key]) grouped[key]=[];
                  grouped[key].push(log);
                }
                const sortedGroups = Object.entries(grouped).sort((a,b)=>b[0].localeCompare(a[0]));
                
                return (
                  <ScrollArea className="h-[450px]">
                    <div className="space-y-0 pr-1">
                      {logs.length === 0 ? (
                        <div className="text-center py-10 text-gray-400">
                          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">No chatter logs yet</p>
                          <p className="text-xs mt-1">Messages from Odoo will appear here</p>
                        </div>
                      ) : sortedGroups.map(([dateKey, entries]) => (
                        <div key={dateKey}>
                          {/* Date separator */}
                          <div className="flex items-center gap-3 my-4">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{fmtGroupDate(dateKey)}</span>
                            <div className="flex-1 h-px bg-border" />
                          </div>
                          {entries.map((log, i) => {
                            const isEmail = ['email','email_outgoing'].includes(log.message_type);
                            const isStatus = !isEmail && (log.is_status_change || log.subtype === 'Stage Changed' || log.subtype?.includes('Stage'));
                            const body = log.body || '';
                            const bodyClean = log.body_clean || body.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
                            
                            // Parse tracking HTML for status changes
                            const changes = [];
                            if (isStatus && body) {
                              const liMatches = body.match(/<li[^>]*>([\s\S]*?)<\/li>/g) || [];
                              for (const li of liMatches) {
                                const s = li.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
                                const m = s.match(/^(.+?)\s*[\u2192>]+\s*(.+?)\s*\((.+?)\)\s*$/);
                                if (m) changes.push({old:m[1].trim(),new:m[2].trim(),field:m[3].trim()});
                              }
                            }
                            
                            return (
                              <div key={log.date+log.author+i} className="flex gap-3 py-2">
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm"
                                  style={{backgroundColor: avatarColor(log.author||'System')}}>
                                  {initials(log.author||'System')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-semibold text-sm text-gray-900">{log.author}</span>
                                    <span className="text-xs text-gray-400">{relTime(log.date)}</span>
                                  </div>
                                  {isEmail ? (
                                    <div className="mt-1">
                                      <button className="w-full text-left px-3 py-2 rounded-t-lg bg-[#e8f4f8] border border-[#b8dde8] text-sm font-medium text-gray-700 italic hover:bg-[#daeef6] transition-colors"
                                        onClick={e=>{const n=e.target.nextSibling;if(n)n.style.display=n.style.display==='none'?'block':'none';}}>
                                        Subject: {log.subject || 'Email notification'} ▼
                                      </button>
                                      <div className="border border-t-0 border-[#b8dde8] rounded-b-lg bg-white p-4 text-sm max-h-80 overflow-y-auto" style={{display:'none'}}
                                        dangerouslySetInnerHTML={{__html: body}} />
                                    </div>
                                  ) : isStatus ? (
                                    <div>
                                      {log.subtype && <p className="text-sm text-gray-700 font-medium">{log.subtype}</p>}
                                      {changes.length > 0 && (
                                        <ul className="mt-1 space-y-0.5">
                                          {changes.map((c,ci)=>(
                                            <li key={ci} className="flex items-center gap-1.5 text-sm">
                                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0"/>
                                              <span className="text-gray-600">{c.old}</span>
                                              <span className="text-[#0ea5e9] font-bold">→</span>
                                              <span className="text-[#0ea5e9] font-semibold">{c.new}</span>
                                              {c.field && <span className="text-gray-400 text-xs">({c.field})</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                      {changes.length===0 && bodyClean && <p className="text-sm text-gray-700 mt-0.5">{bodyClean}</p>}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-800 mt-0.5 leading-relaxed whitespace-pre-line">{bodyClean}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                );
              })()}
            </TabsContent>
            
            {/* Notes Tab */}
            <TabsContent value="notes" className="mt-4 space-y-4">
              <Card>
                <CardContent className="p-3 space-y-3">
                  <div className="flex gap-2">
                    <Select value={newNoteType} onValueChange={setNewNoteType}>
                      <SelectTrigger className="w-28 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="call">Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="update">Update</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      className="flex-1 min-h-[40px]"
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()}>
                      <Send className="h-4 w-4 mr-1" />
                      Add Note
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <ScrollArea className="h-[350px]">
                <div className="space-y-3">
                  {notes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notes yet</p>
                    </div>
                  ) : (
                    notes.map((note) => (
                      <Card key={note.id}>
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-full bg-gray-100">
                              <MessageSquare className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{note.created_by_name || 'Unknown'}</span>
                                <Badge variant="outline" className="text-xs">{note.note_type}</Badge>
                              </div>
                              <p className="text-sm mt-1">{note.content}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Clock className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-400">
                                  {note.created_at ? new Date(note.created_at).toLocaleString() : 'Unknown'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { OpportunityDetailSheet };

// ── Linked Records Panel ───────────────────────────────────────────────────────
// Shows SO & Invoice links for an opportunity with alarms for missing records
function LinkedRecordsPanel({ opportunity, salesOrders, loadSalesOrders, formatCurrency, odooBaseUrl, onNavigateToSO }) {
  const isWon = ['Won', 'won', 'closed_won'].includes(opportunity?.stage);
  const hasSOs = salesOrders.length > 0;
  const [expandedGroup, setExpandedGroup] = React.useState(null);

  // Days since Won (for alarm threshold)
  const daysSinceWon = React.useMemo(() => {
    if (!isWon || !opportunity?.date_last_stage_update) return null;
    return Math.floor((Date.now() - new Date(opportunity.date_last_stage_update).getTime()) / 86400000);
  }, [isWon, opportunity]);

  // SO billing summary
  const totalSOValue = salesOrders.reduce((s, o) => s + (o.amount_total || 0), 0);
  const totalMargin = salesOrders.reduce((s, o) => s + (o.margin || 0), 0);

  // Group by status
  const grouped = React.useMemo(() => {
    const groups = { fully_invoiced: [], partially_invoiced: [], not_invoiced: [] };
    salesOrders.forEach(so => {
      const status = (so.invoice_status || '').toLowerCase();
      if (status === 'fully_invoiced') groups.fully_invoiced.push(so);
      else if (status === 'partially_invoiced') groups.partially_invoiced.push(so);
      else groups.not_invoiced.push(so);
    });
    // Sort each group by SO number descending (newest first)
    Object.values(groups).forEach(g => g.sort((a, b) => (b.so_number || '').localeCompare(a.so_number || '')));
    return groups;
  }, [salesOrders]);

  const STATUS_GROUPS = [
    { key: 'not_invoiced', label: 'Not Invoiced', count: grouped.not_invoiced.length, items: grouped.not_invoiced, badgeBg: 'bg-slate-50', badgeText: 'text-slate-600', dotColor: 'bg-slate-400', borderColor: 'border-slate-200' },
    { key: 'partially_invoiced', label: 'Partially Invoiced', count: grouped.partially_invoiced.length, items: grouped.partially_invoiced, badgeBg: 'bg-amber-50', badgeText: 'text-amber-700', dotColor: 'bg-amber-400', borderColor: 'border-amber-200' },
    { key: 'fully_invoiced', label: 'Fully Invoiced', count: grouped.fully_invoiced.length, items: grouped.fully_invoiced, badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700', dotColor: 'bg-emerald-400', borderColor: 'border-emerald-200' },
  ];

  // Load SOs if not loaded yet when panel mounts
  React.useEffect(() => {
    if (salesOrders.length === 0) loadSalesOrders();
  }, []); // eslint-disable-line

  if (!isWon && !hasSOs) {
    return null;
  }

  const PREVIEW_LIMIT = 4;

  return (
    <div className="rounded-xl border overflow-hidden" data-testid="linked-records-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" /> Linked Records
        </p>
        {odooBaseUrl && opportunity?.source_record_id && (
          <a href={`${odooBaseUrl}/web#id=${opportunity.source_record_id}&model=crm.lead&view_type=form`}
            target="_blank" rel="noopener noreferrer"
            className="text-[10px] text-muted-foreground hover:text-[#800000] flex items-center gap-0.5">
            <ExternalLink className="h-3 w-3" />Odoo #{opportunity.source_record_id}
          </a>
        )}
      </div>

      <div className="p-3 space-y-2 bg-white">
        {/* Summary row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${hasSOs ? 'bg-blue-100' : isWon ? 'bg-red-100' : 'bg-gray-100'}`}>
              <ShoppingCart className={`h-4 w-4 ${hasSOs ? 'text-blue-600' : isWon ? 'text-red-500' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700">
                Sales Orders
                {hasSOs && <span className="ml-1.5 text-blue-600">{salesOrders.length} SO{salesOrders.length !== 1 ? 's' : ''}</span>}
              </p>
              {!hasSOs && isWon ? (
                <p className="text-[10px] text-red-600 font-semibold animate-pulse mt-0.5">
                  No Sales Order created{daysSinceWon ? ` — won ${daysSinceWon} days ago` : ''}
                </p>
              ) : !hasSOs ? (
                <p className="text-[10px] text-muted-foreground mt-0.5">No SO yet (deal not won)</p>
              ) : null}
            </div>
          </div>
          {hasSOs && (
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-slate-800">{formatCurrency(totalSOValue)}</p>
              {totalMargin > 0 && <p className="text-[10px] text-emerald-600">{formatCurrency(totalMargin)} margin</p>}
            </div>
          )}
        </div>

        {/* Grouped SO display */}
        {hasSOs && (
          <div className="space-y-1.5">
            {STATUS_GROUPS.filter(g => g.count > 0).map(group => {
              const isExpanded = expandedGroup === group.key;
              const visible = isExpanded ? group.items : group.items.slice(0, PREVIEW_LIMIT);
              const hasMore = group.items.length > PREVIEW_LIMIT;
              return (
                <div key={group.key} className={`rounded-lg border ${group.borderColor} overflow-hidden`}>
                  {/* Group header */}
                  <div className={`flex items-center justify-between px-3 py-1.5 ${group.badgeBg}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${group.dotColor}`} />
                      <span className={`text-[11px] font-semibold ${group.badgeText}`}>{group.label}</span>
                      <span className={`text-[10px] ${group.badgeText} opacity-70`}>({group.count})</span>
                    </div>
                    <span className="text-[10px] font-mono text-slate-500">
                      {formatCurrency(group.items.reduce((s, o) => s + (o.amount_total || 0), 0))}
                    </span>
                  </div>
                  {/* SO pills */}
                  <div className="flex flex-wrap gap-1 px-3 py-1.5 bg-white">
                    {visible.map(so => (
                      <span key={so.so_number} className={`text-[10px] font-mono font-medium px-1.5 py-0.5 rounded border ${group.borderColor} ${group.badgeBg} ${group.badgeText}`}>
                        {so.so_number}
                      </span>
                    ))}
                    {hasMore && !isExpanded && (
                      <button onClick={() => setExpandedGroup(group.key)} className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${group.badgeText} hover:underline`}>
                        +{group.items.length - PREVIEW_LIMIT} more
                      </button>
                    )}
                    {isExpanded && hasMore && (
                      <button onClick={() => setExpandedGroup(null)} className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${group.badgeText} hover:underline`}>
                        show less
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SO action if Won but no SO */}
        {isWon && !hasSOs && daysSinceWon !== null && (
          <div className={`rounded-lg px-3 py-2 flex items-center justify-between ${daysSinceWon > 30 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
            <p className={`text-xs font-medium ${daysSinceWon > 30 ? 'text-red-700' : 'text-amber-700'}`}>
              {daysSinceWon > 30 ? 'OVERDUE: ' : 'ACTION NEEDED: '}
              Create a Sales Order for this deal in Odoo.
              {daysSinceWon > 30 && ` Deal was won ${daysSinceWon} days ago!`}
            </p>
            {odooBaseUrl && (
              <a href={`${odooBaseUrl}/odoo/sales/new`} target="_blank" rel="noopener noreferrer"
                className={`text-[10px] font-semibold underline whitespace-nowrap ml-2 ${daysSinceWon > 30 ? 'text-red-700' : 'text-amber-700'}`}>
                Create SO →
              </a>
            )}
          </div>
        )}

        {/* View SOs in tab */}
        {hasSOs && (
          <button onClick={onNavigateToSO}
            className="text-[10px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-0.5 w-full justify-end">
            View full SO details → <ExternalLink className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    </div>
  );
}

